import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Button,
  Input,
  Badge,
} from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { titleCase } from "../../utils/case";
import { toast } from "react-toastify";
import {
  getPages,
  updatePage,
  uploadPageImage,
} from "../../api/page.api";
import { getLotOptions, getLotSelectionData } from "../../api/lot.api";
import { emptyLang, parseContent } from "../../utils/pageHelpers";
import General from "./General";
import Home from "./Home";
import Simple from "./Simple";
import Policy from "./Policy";
import Details from "./Details";
import { hasRole } from "../../utils/auth";
import Events from "./Events";

const PAGE_TABS = [
  { key: "general", title: "General" },
  { key: "home", title: "Home" },
  { key: "browse", title: "Browse" },
  { key: "events", title: "Events" },
  { key: "privacy", title: "Privacy Policy" },
  { key: "terms", title: "Terms & Conditions" },
  { key: "refund", title: "Refund Policy" },
  { key: "guest_indemnity", title: "Guest Indemnity" },
];

const Page = () => {
  const { t } = useTranslation();
  const canManage = hasRole(
    "superadmin",
    "finance"
  );
  const [activeTab, setActiveTab] = useState("general");
  const [pages, setPages] = useState({});
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState("");

  const activePage = pages[activeTab] || {};
  const content = activePage.content || {};

  const getDefaultVersion = (page) => {
    const versions = page?.versions || [page];

    return (
      versions.find((v) => v.isPublished) ||
      versions.find((v) => v.status === "published") ||
      versions.find((v) => v.status === "draft") ||
      versions[0] ||
      page
    );
  };

  const normalizePage = (page) => {
    const versions = page?.versions || [page];
    const selected = getDefaultVersion(page);

    return {
      ...selected,
      content: parseContent(selected?.content),
      versions: versions.map((v) => ({
        ...v,
        content: parseContent(v.content),
      })),
    };
  };

  const loadPages = async () => {
    try {
      const res = await getPages();
      const mapped = {};

      (res?.data || res || []).forEach((page) => {
        mapped[page.pageKey] = normalizePage(page);
      });

      setPages(mapped);

      if (mapped.general && activeTab === "general") {
        setSelectedVersionId(String(mapped.general.id || ""));
      }

      setIsDirty(false);

      return mapped;
    } catch (err) {
      toast.error(err.message);
      return {};
    }
  };

  const loadLotSelectionData = async () => {
    try {
      const res = await getLotOptions();
      setLots(res || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    Promise.all([
      loadPages(),
      loadLotSelectionData(),
    ]);
  }, []);

  const getTabVersions = () => {
    const page = pages[activeTab];
    if (!page) return [];
    return page.versions || [page];
  };

  const getPublishedCount = () => getTabVersions().filter((v) => v.status === "published").length;

  const getDraftCount = () => getTabVersions().filter((v) => v.status === "draft").length;

  const handleVersionChange = (versionId) => {
    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Do you want to switch version?"
      );

      if (!confirmed) return;
    }

    const versions = getTabVersions();
    const selected = versions.find((v) => String(v.id) === String(versionId));

    if (!selected) return;

    setSelectedVersionId(versionId);

    setPages((prev) => ({
      ...prev,
      [activeTab]: {
        ...selected,
        content: parseContent(selected.content),
        versions,
      },
    }));

    setIsDirty(false);
  };

  const updateActiveContent = (updater) => {
    setPages((prev) => {
      const page = prev[activeTab] || {};
      const currentContent = page.content || {};
      const nextContent =
        typeof updater === "function" ? updater(currentContent) : updater;

      return {
        ...prev,
        [activeTab]: {
          ...page,
          content: nextContent,
        },
      };
    });

    setIsDirty(true);
  };

  const updateField = (field, value) => {
    updateActiveContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateNestedField = (parent, field, value) => {
    updateActiveContent((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent] || {}),
        [field]: value,
      },
    }));
  };

  const updateLangField = (field, lang, value) => {
    updateActiveContent((prev) => ({
      ...prev,
      [field]: {
        ...(prev[field] || emptyLang()),
        [lang]: value,
      },
    }));
  };

  const updateArrayField = (arrayField, index, field, value) => {
    updateActiveContent((prev) => {
      const list = [...(prev[arrayField] || [])];

      list[index] = {
        ...list[index],
        [field]: value,
      };

      return {
        ...prev,
        [arrayField]: list,
      };
    });
  };

  const updateArrayLangField = (arrayField, index, field, lang, value) => {
    updateActiveContent((prev) => {
      const list = [...(prev[arrayField] || [])];

      list[index] = {
        ...list[index],
        [field]: {
          ...(list[index]?.[field] || emptyLang()),
          [lang]: value,
        },
      };

      return {
        ...prev,
        [arrayField]: list,
      };
    });
  };

  const addArrayItem = (arrayField, item) => {
    updateActiveContent((prev) => ({
      ...prev,
      [arrayField]: [...(prev[arrayField] || []), item],
    }));
  };

  const removeArrayItem = (arrayField, index) => {
    updateActiveContent((prev) => ({
      ...prev,
      [arrayField]: (prev[arrayField] || []).filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;

    try {
      const res = await uploadPageImage(file, "page");
      updateField(field, res.url || res.path || res.data?.url || "");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleArrayImageUpload = async (arrayField, index, field, file) => {
    if (!file) return;

    try {
      const res = await uploadPageImage(file, "page");

      updateArrayField(
        arrayField,
        index,
        field,
        res.url || res.path || res.data?.url || ""
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleTabChange = (key) => {
    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Do you want to switch tabs?"
      );

      if (!confirmed) return;
    }

    const nextPage = pages[key];
    const nextVersion = getDefaultVersion(nextPage);

    setActiveTab(key);
    setSelectedVersionId(String(nextVersion?.id || ""));
    setIsDirty(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      await updatePage(activeTab, {
        id: activePage.id,
        version: activePage.version,
        title: activePage.title,
        content,
        metaTitle: activePage.metaTitle || activePage.title,
        metaDescription: activePage.metaDescription || "",
        status: activePage.status || "draft",
        isPublished: activePage.isPublished || false,
      });

      toast.success("Content saved successfully");
      setIsDirty(false);
      loadPages();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (getDraftCount() >= 1) {
      return toast.error("Only one draft is allowed per page.");
    }

    try {
      setLoading(true);

      await updatePage(activeTab, {
        title: activePage.title,
        content,
        metaTitle: activePage.metaTitle || activePage.title,
        metaDescription: activePage.metaDescription || "",
        status: "draft",
        isPublished: false,
        createDraft: true,
      });

      const mapped = await loadPages();
      const page = mapped[activeTab];
      const versions = page?.versions || [];

      const draft = versions.find((v) => v.status === "draft");

      if (draft) {
        setSelectedVersionId(String(draft.id));

        setPages((prev) => ({
          ...prev,
          [activeTab]: {
            ...draft,
            content: parseContent(draft.content),
            versions,
          },
        }));
      }

      toast.success("Draft created successfully");
      setIsDirty(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (activePage.status === "published" && activePage.isPublished) {
      return toast.info("This version is already published.");
    }

    if (activePage.status !== "published" && getPublishedCount() >= 3) {
      return toast.error("Maximum 3 published versions are allowed per page.");
    }

    const confirmed = window.confirm(
      "Publish this version? This will make it available on the booking portal."
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      await updatePage(activeTab, {
        id: activePage.id,
        version: activePage.version,
        title: activePage.title,
        content,
        metaTitle: activePage.metaTitle || activePage.title,
        metaDescription: activePage.metaDescription || "",
        status: "published",
        isPublished: true,
        publish: true,
      });

      toast.success("Content published successfully");
      setIsDirty(false);
      loadPages();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sharedProps = {
    content,
    lots,
    updateField,
    updateNestedField,
    updateLangField,
    updateArrayField,
    updateArrayLangField,
    addArrayItem,
    removeArrayItem,
    handleImageUpload,
    handleArrayImageUpload,
  };

  const renderContent = () => {
    if (activeTab === "general") {
      return <General {...sharedProps} />;
    }

    if (activeTab === "home") {
      return <Home {...sharedProps} />;
    }

    if (["browse", "inquiry"].includes(activeTab)) {
      return (
        <Simple
          {...sharedProps}
          fields={[
            {
              key: "title",
              label: "title",
            },
            {
              key: "introduction",
              label: "introduction",
              type: "textarea",
            },
          ]}
        />
      );
    }

    if (activeTab === "checkout") {
      return (
        <Simple
          {...sharedProps}
          fields={[
            {
              key: "title",
              label: "title",
            },
            {
              key: "introduction",
              label: "introduction",
              type: "textarea",
            },
            {
              key: "termsNotice",
              label: "terms notice",
              type: "textarea",
            },
          ]}
        />
      );
    }

    if (
      ["privacy", "terms", "refund", "guest_indemnity"].includes(
        activeTab
      )
    ) {
      return <Policy {...sharedProps} />;
    }

    if (activeTab === "events") {
      return <Events {...sharedProps} />;
    }

    if (activeTab === "details") {
      return <Details {...sharedProps} />;
    }

    return null;
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("booking portal"))}
        title={titleCase(t("CMS"))}
        mainTitle={titleCase(t("booking portal content management"))}
      />

      <Container fluid>
        <Row className="project-card">
          <Col className="project-list">
            <Card>
              <CardBody>
                <Nav tabs className="border-tab">
                  {PAGE_TABS.map((tab) => (
                    <NavItem key={tab.key}>
                      <NavLink
                        className={activeTab === tab.key ? "active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleTabChange(tab.key)}
                      >
                        {titleCase(t(tab.title))}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            {canManage && (
              <Card className="mb-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <Input
                      type="select"
                      value={selectedVersionId}
                      onChange={(e) => handleVersionChange(e.target.value)}
                      style={{
                        width: "260px",
                        flexShrink: 0,
                      }}
                    >
                      {getTabVersions().map((version) => (
                        <option key={version.id} value={version.id}>
                          v{version.version} -{" "}
                          {titleCase(t(version.status || "draft"))}
                          {version.isPublished
                            ? ` (${titleCase(t("current"))})`
                            : ""}
                        </option>
                      ))}
                    </Input>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        color={
                          loading || getDraftCount() >= 1
                            ? "muted"
                            : "primary"
                        }
                        outline={!loading}
                        onClick={handleCreateDraft}
                        disabled={loading || getDraftCount() >= 1}
                      >
                        {titleCase(t("new draft"))}
                      </Button>

                      <Button
                        color={loading ? "muted" : "primary"}
                        outline={!loading}
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {loading
                          ? `${titleCase(t("saving"))}...`
                          : titleCase(t("save"))}
                      </Button>

                      <Button
                        color={loading ? "muted" : "success"}
                        onClick={handlePublish}
                        disabled={loading}
                      >
                        {titleCase(t("publish"))}
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1">
                      {titleCase(t(activePage.title || activeTab))}
                    </h5>

                    <small className="text-muted">
                      {titleCase(t("manage booking portal page content"))}
                    </small>
                  </div>

                  {activePage.status && (
                    <Badge
                      color={
                        activePage.status === "published"
                          ? "success"
                          : "warning"
                      }
                    >
                      {titleCase(t(activePage.status))}
                    </Badge>
                  )}
                </div>
              </CardHeader>

              <CardBody>{renderContent()}</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Page;